/**
 * Tiny logging facade. All output is prefixed with [CRG].
 *
 * - debug(): verbose, lifecycle/diagnostic noise (item-changed, mode detection,
 *   drop bookkeeping). Hidden by default; enable in a browser with
 *   localStorage.setItem('crg:debug', '1'), or always on in dev builds.
 * - info(): notable but non-error events (kept sparse - user actions already
 *   surface in the in-app activity log, so prefer debug for routine chatter).
 * - warn()/error(): always shown. These are the "critical action items".
 */
const DEBUG_ENABLED = (() => {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('crg:debug') === '1') {
      return true;
    }
  } catch {
    // localStorage may be unavailable; fall through.
  }
  try {
    return !!import.meta.env?.DEV;
  } catch {
    return false;
  }
})();

const PREFIX = '[CRG]';

// Optional sink: telemetry registers here so every warn/error is also captured
// in App Insights. Kept as a nullable callback (not a direct import) so logger
// stays dependency-free and early boot logs before telemetry init just console.
type LoggerSink = (level: 'warn' | 'error', args: unknown[]) => void;
let sink: LoggerSink | null = null;
export function setLoggerSink(fn: LoggerSink | null): void {
  sink = fn;
}

export const logger = {
  debug: (...args: unknown[]): void => {
    if (DEBUG_ENABLED) console.debug(PREFIX, ...args);
  },
  info: (...args: unknown[]): void => console.log(PREFIX, ...args),
  warn: (...args: unknown[]): void => {
    console.warn(PREFIX, ...args);
    try {
      sink?.('warn', args);
    } catch {
      /* telemetry must never break logging */
    }
  },
  error: (...args: unknown[]): void => {
    console.error(PREFIX, ...args);
    try {
      sink?.('error', args);
    } catch {
      /* telemetry must never break logging */
    }
  },
};
